import React, { useEffect, useRef } from 'react';
import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import {
  SHORT_NAME_MAX,
  SHORT_NAME_MIN,
  generateCatalogShortName,
  isValidCatalogShortName
} from '../utils/catalogShortName';

/**
 * Auto-generates a 12–16 char shortname from `name`, with optional manual edit via checkbox.
 */
const CatalogShortNameField = ({
  name,
  shortName,
  onShortNameChange,
  editEnabled,
  onEditEnabledChange,
  disabled = false
}) => {
  const onChangeRef = useRef(onShortNameChange);
  onChangeRef.current = onShortNameChange;

  useEffect(() => {
    if (editEnabled || disabled) return;
    if (!name) return;
    const next = generateCatalogShortName(name);
    onChangeRef.current(next);
  }, [name, editEnabled, disabled]);

  const valid = !shortName || isValidCatalogShortName(shortName);

  return (
    <Box>
      <FormControlLabel
        control={(
          <Checkbox
            checked={editEnabled}
            disabled={disabled}
            onChange={(e) => onEditEnabledChange(e.target.checked)}
          />
        )}
        label="Edit shortname"
      />
      <TextField
        label={`Shortname (${SHORT_NAME_MIN}–${SHORT_NAME_MAX} chars)`}
        value={shortName}
        onChange={(e) => onShortNameChange(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, SHORT_NAME_MAX))}
        disabled={disabled || !editEnabled}
        fullWidth
        error={Boolean(shortName) && !valid}
        helperText={
          !valid
            ? `Must be ${SHORT_NAME_MIN}–${SHORT_NAME_MAX} lowercase alphanumeric characters`
            : 'Used in URLs as {tenant}-{service}.{env}.{software}.pilvisystems.com — e.g. erp4c'
        }
      />
      {!editEnabled && shortName ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Auto-generated from name. Check &quot;Edit shortname&quot; to customize.
        </Typography>
      ) : null}
    </Box>
  );
};

export default CatalogShortNameField;
